import { useState, useEffect, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Buildings } from "@phosphor-icons/react";

interface BuildingLink {
  id: number;
  buildingId: string;
  reason: string;
  buildingAddress: string | null;
  buildingHousenr: string | null;
}

interface ContactBuildingLinksSectionProps {
  contactId?: number;
  companyId?: number;
}

export const ContactBuildingLinksSection = ({
  contactId,
  companyId,
}: ContactBuildingLinksSectionProps) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [links, setLinks] = useState<BuildingLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLinks = useCallback(async () => {
    if (!session) return;
    const url = contactId
      ? `${API_URL}/contact/${contactId}/building-links`
      : `${API_URL}/contact/company/${companyId}/building-links`;
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(url, { method: "GET" }, session);
      if (response.ok) {
        setLinks(await response.json());
      }
    } catch (error) {
      console.error("Error fetching building links:", error);
    } finally {
      setIsLoading(false);
    }
  }, [session, contactId, companyId]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  if (isLoading || links.length === 0) return null;

  return (
    <div className="border-neutral-primary/15 flex flex-col gap-3 rounded-md border bg-white p-4">
      <div className="flex items-center gap-2">
        <Buildings weight="duotone" size={18} className="text-moss-primary" />
        <h3 className="text-sm font-semibold text-neutral-900">
          Actief in gebouwen
        </h3>
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-moss-primary/10 px-1.5 text-xs font-semibold text-moss-primary">
          {links.length}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {links.map((link) => {
          const address =
            link.buildingAddress ||
            `${link.buildingId}${link.buildingHousenr ? ` ${link.buildingHousenr}` : ""}`;
          return (
            <div
              key={link.id}
              className="flex flex-col gap-0.5 rounded-md border border-neutral-100 bg-neutral-50 px-3 py-2"
            >
              <button
                onClick={() => navigate(`/building/${link.buildingId}`)}
                className="text-left text-sm font-medium text-moss-primary hover:underline"
              >
                {address}
              </button>
              {link.reason && (
                <span className="text-xs text-neutral-500">{link.reason}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
